import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { useBrowseLabel } from '@aibees/elephant/hooks/labels/use-browse-label';
import { usePageType } from '@aibees/elephant/hooks/use-page-type';
import { SearchType } from '@aibees/elephant/types';
import { LabelGrouping, LabelType } from '@aibees/elephant/types/label';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Styled from './styles';
import * as SX from './sx';
import type { RecordManagementPopoverHeaderProps as Props } from './types';

export const RecordManagementPopoverHeader = memo(
  ({ className, route, title, showCreateSection, onCreateNew }: Props) => {
    const type = useSearchTypeFromRoute();
    const { isPeoplePage } = usePageType();

    const [contactCount, setContactCount] = useState(0);
    const [accountCount, setAccountCount] = useState(0);

    const { data: personData } = useBrowseLabel({
      type: title === 'Lists' ? LabelType.LIST : LabelType.LABEL,
      grouping: LabelGrouping.PEOPLE,
    });

    const { data: companyData } = useBrowseLabel({
      type: title === 'Lists' ? LabelType.LIST : LabelType.LABEL,
      grouping: LabelGrouping.COMPANY,
    });

    useEffect(() => {
      if (personData?.content) {
        setContactCount(personData?.content?.length);
      }
      if (companyData?.content) {
        setAccountCount(companyData?.content?.length);
      }
    }, [personData, companyData]);

    return (
      <Styled.Wrapper
        className={classnames('RecordManagementPopoverHeader', className)}
        sx={SX.root}
      >
        <Stack direction="row" gap={1}>
          {type === SearchType.People && (
            <Stack sx={SX.accountTitle}>
              <Icon name="supervisor" size={18} />
              <Typography variant="body4">
                Contact {title} ({contactCount})
              </Typography>
            </Stack>
          )}
          <Stack
            sx={mergeSx(
              SX.accountTitle,
              (type === SearchType.People || isPeoplePage) && {
                backgroundColor: (p) => p.palette.grey[200],
                color: (p) => p.palette.grey[500],
                pointerEvents: 'none',
                opacity: 0.6,
              }
            )}
          >
            <Icon name="business" size={18} />
            <Typography variant="body4">
              Account {title} ({accountCount})
            </Typography>
          </Stack>
        </Stack>
        {showCreateSection ? (
          <Link to={route} target="_blank">
            <Button
              color="primary"
              endIcon={<Icon name="setting-1" size={14} color="primary" />}
            >
              <Typography sx={SX.action}>Manage</Typography>
            </Button>
          </Link>
        ) : (
          <Button variant="text" sx={SX.accountButton} onClick={onCreateNew}>
            Create New
          </Button>
        )}
      </Styled.Wrapper>
    );
  }
);

RecordManagementPopoverHeader.displayName = 'RecordManagementPopoverHeader';
